import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { GoChevronRight } from "react-icons/go";
import { LuUser } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";
import {
  DeviceSplitInteractions,
  InteractionsTaken,
  NewRecordingSessionQueryResultsGroupedType,
  RecordingSessionQueryResultsGroupedType,
  RecordingSessionType,
  useGetGroupedRecordingSessionsForUserLazyQuery,
} from "../__generated__/graphql";
import ApplicationPage from "../components/ApplicationPage";
import FilterList from "../components/FilterList";
import GroupedRecordingsTimeline from "../components/GroupedRecordingsTimeline";
import LoadingAnimation from "../components/LoadingAnimation";
import Pagination from "../components/Pagination";
import RRWebPlayerWithTriage from "../components/RRWebPlayerWithTriage";
import RecordingsSearchAndFilters from "../components/Search/RecordingsSearchAndFilters";
import { useCompany } from "../contexts/CompanyContext";
import { WebPlayerProvider } from "../contexts/WebPlayerContext";
import useContainerSize from "../hooks/useContainerSize";
import useUnzippedEvents from "../hooks/useUnzippedEvents";
import {
  DEVICE_TYPES,
  DOCS_DOMAIN,
  RECORDINGS_EMPTY_FILTERS,
} from "../utils/constants";
import { DeviceType, RecordingsFilters } from "../utils/interfaces";

const RECORDINGS_PER_PAGE = 15;
const RECORDING_LOOKBACK_DAYS = 14;

const findFirstActivityOfLastDay = (userInteractions: InteractionsTaken) => {
  const tempTimelineEvents: { timestamp: number; recordingId: string }[] = [];

  userInteractions.clicks.forEach((click) => {
    if (click.timestamp === undefined) return;
    tempTimelineEvents.push({
      timestamp: +(click.timestamp || ""),
      recordingId: click.recordingId,
    });
  });

  userInteractions.pageNavigations.forEach((pageNavigation) => {
    if (pageNavigation.timestamp === undefined) return;
    tempTimelineEvents.push({
      timestamp: +(pageNavigation.timestamp || ""),
      recordingId: pageNavigation.recordingId,
    });
  });

  userInteractions.textEdits.forEach((textEdit) => {
    if (textEdit.timestamp === undefined) return;
    tempTimelineEvents.push({
      timestamp: +(textEdit.timestamp || ""),
      recordingId: textEdit.recordingId,
    });
  });

  const sortedEvents = tempTimelineEvents.sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  let lastEvent: { timestamp: number; recordingId: string } | undefined;
  let lookbackDate = DateTime.local();

  for (let i = 0; i < RECORDING_LOOKBACK_DAYS; i++) {
    const startOfDay = lookbackDate.startOf("day").toMillis();
    const endOfDay = lookbackDate.endOf("day").toMillis();

    lastEvent = sortedEvents.find(
      (event) => event.timestamp >= startOfDay && event.timestamp <= endOfDay,
    );

    if (lastEvent) {
      return lastEvent;
    }

    lookbackDate = lookbackDate.minus({ days: 1 });
  }
};

const ReplaysWithSearchAndExaminePage = () => {
  const rrwebPlayerRef = useRef<any>(null);
  const { selectedCompany } = useCompany();
  const [fetchGroupedRecordingForUser, { loading: recordingLoading }] =
    useGetGroupedRecordingSessionsForUserLazyQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRecording, setSelectedRecording] =
    useState<RecordingSessionType | null>(null);
  const pageInQuery = Number(searchParams.get("page"));
  const [page, setPage] = useState(pageInQuery || 1);
  const [recordings, setRecordings] = useState<
    NewRecordingSessionQueryResultsGroupedType[]
  >([]);
  const [selectedRecordings, setSelectedRecordings] =
    useState<RecordingSessionQueryResultsGroupedType | null>(null);
  const [userInteractions, setUserInteractions] =
    useState<DeviceSplitInteractions | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [selectedDeviceRecordings, setSelectedDeviceRecordings] = useState<
    RecordingSessionType[] | null
  >(null);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Select recording after fetching the list. -1 - select last item, 1 select first item, 0 don't select
  const [selectRecordingAfterFetch, setSelectRecordingAfterFetch] = useState(0);
  const [filters, setFilters] = useState<RecordingsFilters>(() => {
    const pageVisitUrlsParam = searchParams.get("pageVisitUrls");
    const devicesParam = searchParams.get("devices");
    const userParam = searchParams.get("user");

    return {
      pageVisitUrls: pageVisitUrlsParam
        ? pageVisitUrlsParam.split(",")
        : RECORDINGS_EMPTY_FILTERS.pageVisitUrls,
      devices: devicesParam
        ? (devicesParam.split(",") as DeviceType[])
        : RECORDINGS_EMPTY_FILTERS.devices,
      user: userParam || RECORDINGS_EMPTY_FILTERS.user,
    };
  });
  const [timeline, setTimeline] = useState<[number, number]>([0, 0]);
  const [initialTime, setInitialTime] = useState<number | undefined>();
  const [pageId, setPageId] = useState<string | undefined>();

  const {
    unzippedEvents,
    eventsError,
    loadingEvents,
    fetchUnzippedEvents,
    setUnzippedEvents,
    setTimelineEvents,
  } = useUnzippedEvents(selectedRecording?.id, rrwebPlayerRef, pageId);
  const { containerRef, containerWidth, containerHeight } = useContainerSize();

  const handleSelectRecording = useCallback(
    (recording: RecordingSessionType, fetchOnly?: boolean, pageId?: string) => {
      if (fetchOnly) {
        fetchUnzippedEvents({
          variables: {
            id: recording.id,
            pageId,
            ...(selectedCompany && {
              companyId: parseInt(selectedCompany.value, 10),
            }),
          },
        });
        return;
      }
      if (selectedRecording?.id === recording.id) {
        // Same recording selected, seek back to the start and keep playing
        rrwebPlayerRef.current?.resetPlayer();
      } else {
        setUnzippedEvents(null); // Reset events to force RRWebPlayer re-initialization
        setTimelineEvents([]);
        setSelectedRecording(recording);
      }
    },
    [
      fetchUnzippedEvents,
      selectedCompany,
      selectedRecording?.id,
      setTimelineEvents,
      setUnzippedEvents,
    ],
  );

  const handleRecordingGroupChange = async (
    userId: string,
    device: DeviceType,
    currentTimestamp?: number | null,
  ) => {
    const { data } = await fetchGroupedRecordingForUser({
      variables: {
        companyId: selectedCompany ? parseInt(selectedCompany.value, 10) : 0,
        userId,
      },
    });
    if (data) {
      setInitialTime(undefined);
      const sessions = data.getGroupedRecordingSessionsForUser.recordings;
      const deviceRecordings =
        device === DEVICE_TYPES.DESKTOP
          ? (sessions.filter(
              (recording: any) => !recording.metadata?.device?.isMobile,
            ) as RecordingSessionType[])
          : (sessions.filter(
              (recording: any) => recording.metadata?.device?.isMobile,
            ) as RecordingSessionType[]);
      setSelectedRecordings(
        data.getGroupedRecordingSessionsForUser as RecordingSessionQueryResultsGroupedType,
      );
      const allUserInteractions = data.getGroupedRecordingSessionsForUser
        .userInteractions as DeviceSplitInteractions;
      setUserInteractions(allUserInteractions);
      setSelectedDeviceRecordings(deviceRecordings);
      let recording =
        currentTimestamp &&
        deviceRecordings.find(
          ({ startTimestamp, durationMs }) =>
            currentTimestamp >= startTimestamp &&
            currentTimestamp <= startTimestamp + durationMs,
        );
      let timestampToUse = currentTimestamp;

      if (!recording) {
        const userInteractions =
          device === DEVICE_TYPES.DESKTOP
            ? allUserInteractions?.desktop
            : allUserInteractions?.mobile;
        const firstActivityOfLastDay =
          findFirstActivityOfLastDay(userInteractions);
        recording = deviceRecordings.find(
          ({ id }) => id === firstActivityOfLastDay?.recordingId,
        );
        timestampToUse = firstActivityOfLastDay?.timestamp;
      }

      if (!recording) {
        recording = deviceRecordings[0];
      }

      let pageId = recording.pageVisits[0]?.id;
      if (timestampToUse) {
        const ts = timestampToUse;
        rrwebPlayerRef.current
          ? rrwebPlayerRef.current.goToTime(ts)
          : setInitialTime(ts);
        pageId = recording.pageVisits.find(
          ({ activeFrom, activeUntil }) =>
            ts >= +activeFrom && (!activeUntil || ts < +activeUntil),
        )?.id;
      }
      setPageId(pageId);
      handleSelectRecording(recording);
      setSelectedDevice(device);

      const params = new URLSearchParams(searchParams);
      params.set("selectedUser", userId);
      params.set("device", device);
      setSearchParams(params);
    }
  };

  const selectedUser = searchParams.get("selectedUser");

  const handleSearchResults = (results: any[]) => {
    const endDate = Date.now();
    setTimeline([
      endDate - RECORDING_LOOKBACK_DAYS * 24 * 3600 * 1000,
      endDate,
    ]);
    setRecordings(results);
    if (results.length && selectRecordingAfterFetch) {
      setSelectRecordingAfterFetch(0);
      handleRecordingGroupChange(
        results[selectRecordingAfterFetch > 0 ? 0 : results.length - 1].userId,
        selectedDevice || "Desktop",
      );
      return;
    }
  };

  const handleTotalItemsCount = (count: number) => {
    setTotalItemsCount(count); // Update the total items count
  };

  const totalPages = Math.ceil(totalItemsCount / RECORDINGS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSearchParams((params) => {
      params.set("page", `${newPage}`);
      return params;
    });
  };

  useEffect(() => {
    // Calculate the new total pages based on the updated totalItemsCount
    const newTotalPages = Math.ceil(totalItemsCount / RECORDINGS_PER_PAGE);

    // If the current page exceeds the new total pages, set the page to the new max page
    if (page > newTotalPages && newTotalPages > 0) {
      setPage(newTotalPages);
      setSearchParams((params) => {
        params.set("page", `${newTotalPages}`);
        return params;
      });
    }
  }, [page, setSearchParams, totalItemsCount]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (filters.pageVisitUrls.length > 0) {
      params.set("pageVisitUrls", filters.pageVisitUrls.join(","));
    } else {
      params.delete("pageVisitUrls");
    }

    if (filters.devices.length > 0) {
      params.set("devices", filters.devices.join(","));
    } else {
      params.delete("devices");
    }

    if (filters.user) {
      params.set("user", filters.user);
    } else {
      params.delete("user");
    }

    setSearchParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!selectedUser || selectedRecordings?.userId === selectedUser) return;
    const device =
      (searchParams.get("device") as DeviceType) ||
      selectedDevice ||
      DEVICE_TYPES.DESKTOP;
    const timestamp = searchParams.get("timestamp");
    handleRecordingGroupChange(
      selectedUser,
      device,
      timestamp ? parseInt(timestamp, 10) : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentRecordingIndex = recordings.findIndex(
    ({ userId }) => userId === selectedRecordings?.userId,
  );
  const isFirstRecording = currentRecordingIndex === 0;
  const isLastRecording = currentRecordingIndex === recordings.length - 1;

  const handleBackwardRecording = () => {
    if (isFirstRecording) {
      setSelectRecordingAfterFetch(-1);
      handlePageChange(page - 1);
    } else {
      handleRecordingGroupChange(
        recordings[currentRecordingIndex - 1].userId,
        selectedDevice || "Desktop",
      );
    }
  };

  const handleForwardRecording = () => {
    if (isLastRecording) {
      setSelectRecordingAfterFetch(1);
      handlePageChange(page + 1);
    } else {
      handleRecordingGroupChange(
        recordings[currentRecordingIndex + 1].userId,
        selectedDevice || "Desktop",
      );
    }
  };

  const resetState = () => {
    setUnzippedEvents(null);
    setTimelineEvents([]);
    setSelectedRecording(null);
    setSelectedRecordings(null);
    setSelectedDeviceRecordings(null);
    setInitialTime(undefined);
    setPageId(undefined);
    setSelectedDevice(null);

    const searchParamsToKeep = ["page", "pageVisitUrls", "devices", "user"];
    const newParams = new URLSearchParams();

    searchParamsToKeep.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams);
  };

  const backwardRecordingDisabled = isFirstRecording && page === 1;
  const forwardRecordingDisabled = isLastRecording && page === totalPages;

  const showRecording = recordingLoading || selectedRecording;
  const user = selectedRecordings?.userId || selectedUser;

  return (
    <ApplicationPage
      pageTitle={user ? `Recordings - ${user}` : "Recordings"}
      pageHeaderContent={
        <div className='flex gap-1 items-center'>
          {user ? (
            <>
              <button onClick={resetState}>Recordings</button>
              <GoChevronRight />
              <Autocomplete
                size='small'
                className='w-80'
                disableClearable
                options={[
                  ...new Set([user, ...recordings.map(({ userId }) => userId)]),
                ]}
                value={user}
                onChange={(_, newValue) =>
                  handleRecordingGroupChange(
                    newValue,
                    selectedDevice || DEVICE_TYPES.DESKTOP,
                  )
                }
                classes={{
                  input: "!py-[1px] !px-0 !h-[18px]",
                  inputRoot: "!text-xs !py-1",
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position='start'>
                          <LuUser size={14} color='black' />
                        </InputAdornment>
                      ),
                    }}
                    variant='outlined'
                  />
                )}
                data-testid='selectUser'
              />
            </>
          ) : (
            "Recordings"
          )}
        </div>
      }
      helpLink={DOCS_DOMAIN}
      helpTooltip='Click here to learn about Recordings.'
    >
      {showRecording && (
        <div className='h-[calc(100%_-_44px)] mb-4'>
          <WebPlayerProvider>
            <div ref={containerRef} className='h-full'>
              {selectedRecording && (
                <RRWebPlayerWithTriage
                  id={selectedRecording.id}
                  selectedRecording={selectedRecording}
                  ref={rrwebPlayerRef}
                  events={unzippedEvents}
                  highlightedSections={[]}
                  width={containerWidth - 16}
                  containerHeight={containerHeight}
                  containerWidth={containerWidth}
                  eventsError={eventsError}
                  loadingEvents={loadingEvents}
                  handleBackwardRecording={handleBackwardRecording}
                  handleForwardRecording={handleForwardRecording}
                  backwardRecordingDisabled={backwardRecordingDisabled}
                  forwardRecordingDisabled={forwardRecordingDisabled}
                  handleSelectRecording={handleSelectRecording}
                  selectedRecordings={selectedRecordings}
                  timeline={timeline}
                  selectedDevice={selectedDevice}
                  selectedDeviceRecordings={selectedDeviceRecordings}
                  initialTime={initialTime}
                  shouldSkipBackgroundActivity
                  userInteractions={userInteractions}
                  onPlay={(device, timestamp) => {
                    if (!selectedRecordings) return;
                    const deviceRecordings =
                      device === DEVICE_TYPES.DESKTOP
                        ? (selectedRecordings.recordings.filter(
                            (recording: any) =>
                              !recording.metadata?.device?.isMobile,
                          ) as RecordingSessionType[])
                        : (selectedRecordings.recordings.filter(
                            (recording: any) =>
                              recording.metadata?.device?.isMobile,
                          ) as RecordingSessionType[]);
                    setSelectedDeviceRecordings(deviceRecordings);
                    setSelectedDevice(device);

                    const params = new URLSearchParams(searchParams);
                    params.set("device", device);
                    setSearchParams(params);

                    const recording = deviceRecordings.find(
                      ({ startTimestamp, durationMs }) =>
                        timestamp >= startTimestamp &&
                        timestamp <= startTimestamp + durationMs,
                    );
                    if (recording) {
                      const pageId = recording.pageVisits.find(
                        ({ activeFrom, activeUntil }) =>
                          timestamp >= +activeFrom &&
                          (!activeUntil || timestamp < +activeUntil),
                      )?.id;
                      setPageId(pageId);
                      handleSelectRecording(recording);
                      rrwebPlayerRef.current.goToTime(timestamp);
                      return;
                    }
                    const nextRecording = deviceRecordings.find(
                      ({ startTimestamp }) => startTimestamp > timestamp,
                    );
                    const r = nextRecording || deviceRecordings[0];
                    setPageId(r.pageVisits[0]?.id);
                    handleSelectRecording(r);
                  }}
                />
              )}
              {recordingLoading && !selectedRecording && (
                <LoadingAnimation text='Loading...' />
              )}
            </div>
          </WebPlayerProvider>
        </div>
      )}
      <div
        className={`flex pb-4 ${showRecording ? "" : "h-[calc(100%_-_44px)]"}`}
      >
        <div className='w-48 min-w-48 border-r border-zinc-200 pr-4'>
          <RecordingsSearchAndFilters
            entity='Recordings'
            limit={RECORDINGS_PER_PAGE}
            offset={(page - 1) * RECORDINGS_PER_PAGE}
            onSearchResults={handleSearchResults}
            setTotalItemsCount={handleTotalItemsCount}
            onLoadingChange={setLoading}
            orderBy='-start_timestamp'
            filters={filters}
            setFilters={setFilters}
          />
        </div>
        <div className='flex flex-col min-w-0 flex-1 ml-4 gap-2'>
          <div className='min-h-8'>
            <FilterList
              filters={{
                ...filters,
                user: filters.user ? [filters.user] : [],
              }}
              filtersLabels={{
                pageVisitUrls: "URL",
                devices: "Device",
                user: "User",
              }}
              onRemove={(key, value) =>
                setFilters((prev) => ({
                  ...prev,
                  [key]:
                    key === "user"
                      ? null
                      : prev[key].filter((v) => v !== value),
                }))
              }
            />
          </div>
          {loading ? (
            <LoadingAnimation text='Loading...' />
          ) : (
            <div
              className='flex flex-col gap-3 text-xs'
              data-testid='recordingsListContainer'
            >
              {recordings.map((r) => (
                <div
                  key={r.userId}
                  className='flex gap-2 border-b border-slate-300 pb-3'
                  data-testid={`recordingItem - ${r.userId}`}
                >
                  <Tooltip title={r.userId} placement='top'>
                    <div
                      className='w-28 truncate font-medium'
                      data-testid='recordingItemUser'
                      onClick={() => {
                        const device = r.sessions.sessionItems.some(
                          (recording) => !recording.deviceInfo?.isMobile,
                        )
                          ? DEVICE_TYPES.DESKTOP
                          : DEVICE_TYPES.MOBILE;
                        handleRecordingGroupChange(r.userId, device);
                      }}
                      role='button'
                      tabIndex={0}
                    >
                      {r.userId}
                    </div>
                  </Tooltip>
                  <GroupedRecordingsTimeline
                    recordings={r.sessions}
                    selected={
                      selectedRecordings?.userId === r.userId && selectedDevice
                    }
                    timeline={timeline}
                    onPlay={(device, timestamp) =>
                      handleRecordingGroupChange(r.userId, device, timestamp)
                    }
                  />
                </div>
              ))}
              {filters.user && !recordings.length && (
                <div className='text-base'>
                  The user doesn't have any activity in the last 2 weeks
                </div>
              )}
            </div>
          )}
          <div className='flex flex-1 items-end'>
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </ApplicationPage>
  );
};

export default ReplaysWithSearchAndExaminePage;
